import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CircleF,
  GoogleMap,
  InfoWindowF,
  OVERLAY_MOUSE_TARGET,
  OverlayViewF,
  useJsApiLoader
} from '@react-google-maps/api';
import clsx from 'clsx';
import {
  ActivityCoordinate,
  ActivityLocationCoverage,
  ActivityMarkerGroup,
  ActivityTimelineItem
} from './activityHistory';

type Props = {
  apiKey: string;
  markerGroups: ActivityMarkerGroup[];
  timeline: ActivityTimelineItem[];
  locationCoverage: ActivityLocationCoverage | null;
  selectedMarkerId: string | null;
  focusRequest: number;
  onMarkerSelect: (markerId: string) => void;
  onMarkerClose: () => void;
};

declare global {
  interface Window {
    gm_authFailure?: () => void;
  }
}

const MAP_OPTIONS: google.maps.MapOptions = {
  clickableIcons: false,
  disableDefaultUI: true,
  fullscreenControl: false,
  gestureHandling: 'cooperative',
  mapTypeControl: false,
  streetViewControl: false,
  zoomControl: true,
  styles: [
    {
      elementType: 'geometry',
      stylers: [{ color: '#E9DDCB' }]
    },
    {
      elementType: 'labels.text.fill',
      stylers: [{ color: '#667085' }]
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [{ color: '#F9FAFB' }]
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [{ color: '#FFFFFF' }]
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [{ color: '#F4F6F8' }]
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{ color: '#DCE9EC' }]
    },
    {
      featureType: 'poi',
      elementType: 'geometry',
      stylers: [{ color: '#F7D59A' }]
    }
  ]
};

const ACTIVITY_COVERAGE_OPTIONS: google.maps.CircleOptions = {
  clickable: false,
  fillColor: '#667085',
  fillOpacity: 0.18,
  strokeColor: '#667085',
  strokeOpacity: 0.35,
  strokeWeight: 1,
  zIndex: 0
};

const METRES_PER_LATITUDE_DEGREE = 111_320;
const CITY_OVERVIEW_ZOOM = 11;
const DETAIL_ZOOM = 16;
const MARKER_FOCUS_ZOOM = 17;
const CITY_OVERVIEW_MAX_SPAN_METRES = 20_000;
const MAP_CONTROL_BUTTON_CLASS =
  'rounded-md border border-grey-300 bg-white px-3 py-1.5 text-xs font-semibold text-grey-700 shadow-paper hover:bg-grey-50';

const centerOverlay = (width: number, height: number) => ({
  x: -(width / 2),
  y: -(height - 30)
});

const getCoverageBoundaryPoints = ({
  center,
  radiusMetres
}: ActivityLocationCoverage): ActivityCoordinate[] => {
  const latitudeDelta = radiusMetres / METRES_PER_LATITUDE_DEGREE;
  const longitudeMetresPerDegree =
    METRES_PER_LATITUDE_DEGREE * Math.cos((center.lat * Math.PI) / 180);
  const longitudeDelta =
    radiusMetres / Math.max(Math.abs(longitudeMetresPerDegree), 1);

  return [
    { lat: center.lat - latitudeDelta, lng: center.lng },
    { lat: center.lat + latitudeDelta, lng: center.lng },
    { lat: center.lat, lng: center.lng - longitudeDelta },
    { lat: center.lat, lng: center.lng + longitudeDelta }
  ];
};

const fitLocations = (
  map: google.maps.Map,
  locations: ActivityCoordinate[],
  coverage: ActivityLocationCoverage | null
) => {
  const boundsLocations = coverage
    ? [...locations, ...getCoverageBoundaryPoints(coverage)]
    : locations;
  const bounds = new google.maps.LatLngBounds();
  boundsLocations.forEach((location) => bounds.extend(location));
  map.fitBounds(bounds, 56);
};

const MarkerTooltip = ({ names }: { names: string[] }) => (
  <span
    role="tooltip"
    className="pointer-events-none absolute bottom-[calc(100%+6px)] left-1/2 z-20 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-grey-900 px-3 py-2 text-xs font-medium text-white shadow-paper group-hover:block group-focus-within:block">
    {names.join(', ')}
  </span>
);

const NumberedMarker = ({
  marker,
  selected,
  onSelect
}: {
  marker: ActivityMarkerGroup;
  selected: boolean;
  onSelect: () => void;
}) => {
  const numberLabel = marker.markerNumbers.join(', ');
  const documentLabel = marker.documentNames.join(', ');

  return (
    <OverlayViewF
      mapPaneName={OVERLAY_MOUSE_TARGET}
      position={marker.location}
      getPixelPositionOffset={centerOverlay}>
      <span className="group relative block">
        <button
          type="button"
          aria-label={`Map marker ${numberLabel}: ${documentLabel}`}
          aria-pressed={selected}
          onClick={onSelect}
          className={clsx(
            'flex min-h-7 min-w-7 items-center justify-center rounded-full border-2 border-white px-2 text-xs font-semibold text-white shadow-paper transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-secondary-light focus:ring-offset-2',
            marker.tone === 'highlight' ? 'bg-secondary-light' : 'bg-grey-700',
            selected && 'scale-110 ring-4 ring-secondary-light/30'
          )}>
          {numberLabel}
        </button>
        <MarkerTooltip names={marker.documentNames} />
      </span>
    </OverlayViewF>
  );
};

const SelectedMarkerDetails = ({
  items
}: {
  items: ActivityTimelineItem[];
}) => (
  <div
    aria-label="Selected document submission details"
    className="w-[260px] max-w-full pr-1 text-grey-700">
    <p className="text-sm font-semibold">
      {items.length === 1
        ? 'Document submission'
        : `${items.length} submissions at this location`}
    </p>
    <ol className="mt-2 divide-y divide-grey-100">
      {items.map((item) => (
        <li key={item.id} className="flex gap-2 py-2 first:pt-0 last:pb-0">
          <span
            className={clsx(
              'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white',
              item.markerTone === 'highlight'
                ? 'bg-secondary-light'
                : 'bg-grey-700'
            )}>
            {item.markerNumber}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold">{item.documentName}</p>
            <time
              className="mt-0.5 block text-[11px] text-grey-500"
              dateTime={item.submittedAt}>
              {item.exactTimestamp}
            </time>
          </div>
          <span className="flex-shrink-0 text-[11px] font-semibold text-grey-600">
            {item.elapsedLabel}
          </span>
        </li>
      ))}
    </ol>
  </div>
);

const MapStatus = ({
  title,
  description
}: {
  title: string;
  description?: string;
}) => (
  <div
    className="flex h-full min-h-[440px] flex-col items-center justify-center bg-grey-50 px-6 text-center xl:min-h-[600px]"
    role="status">
    <p className="text-sm font-semibold text-grey-700">{title}</p>
    {description && (
      <p className="mt-1 max-w-md text-xs text-grey-500">{description}</p>
    )}
  </div>
);

const LoadedActivityHistoryMap = ({
  apiKey,
  markerGroups,
  timeline,
  locationCoverage,
  selectedMarkerId,
  focusRequest,
  onMarkerSelect,
  onMarkerClose
}: Props) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'activity-history-google-map-script',
    googleMapsApiKey: apiKey
  });
  const selectedMarker =
    markerGroups.find((marker) => marker.id === selectedMarkerId) ?? null;
  const selectedMarkerItems = selectedMarker
    ? timeline.filter((item) =>
        selectedMarker.markerNumbers.includes(item.markerNumber)
      )
    : [];

  const focusSelectedMarker = useCallback(
    (map: google.maps.Map) => {
      if (!selectedMarker) {
        return;
      }

      map.panTo(selectedMarker.location);
      const currentZoom = map.getZoom();
      if (currentZoom == null || currentZoom < MARKER_FOCUS_ZOOM) {
        map.setZoom(MARKER_FOCUS_ZOOM);
      }
    },
    [selectedMarker]
  );

  const applyCityViewport = useCallback(
    (map: google.maps.Map) => {
      const locations = markerGroups.map((marker) => marker.location);
      const spanMetres = locationCoverage?.maximumSpanMetres ?? 0;

      if (locations.length > 1 && spanMetres > CITY_OVERVIEW_MAX_SPAN_METRES) {
        fitLocations(map, locations, locationCoverage);
        return;
      }

      map.setCenter(locationCoverage?.center ?? locations[0]);
      map.setZoom(CITY_OVERVIEW_ZOOM);
    },
    [locationCoverage, markerGroups]
  );

  const applyDetailViewport = useCallback(
    (map: google.maps.Map) => {
      const locations = markerGroups.map((marker) => marker.location);

      if (locations.length === 1) {
        map.setCenter(locations[0]);
        map.setZoom(DETAIL_ZOOM);
        return;
      }

      fitLocations(map, locations, locationCoverage);
    },
    [locationCoverage, markerGroups]
  );

  const handleMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      applyCityViewport(map);
      focusSelectedMarker(map);
    },
    [applyCityViewport, focusSelectedMarker]
  );

  const handleMapUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const handleResetView = useCallback(() => {
    if (mapRef.current) {
      applyCityViewport(mapRef.current);
      onMarkerClose();
    }
  }, [applyCityViewport, onMarkerClose]);

  const handleZoomToDetails = useCallback(() => {
    if (mapRef.current) {
      applyDetailViewport(mapRef.current);
    }
  }, [applyDetailViewport]);

  useEffect(() => {
    if (mapRef.current) {
      focusSelectedMarker(mapRef.current);
    }
  }, [focusRequest, focusSelectedMarker]);

  if (loadError) {
    return (
      <MapStatus
        title="Map unavailable"
        description="Google Maps could not be loaded. The document timeline is still available."
      />
    );
  }

  if (!isLoaded) {
    return <MapStatus title="Loading activity map..." />;
  }

  return (
    <div className="relative h-full min-h-[440px] w-full xl:min-h-[600px]">
      <div className="absolute left-3 top-3 z-10 flex gap-2">
        <button
          type="button"
          onClick={handleZoomToDetails}
          aria-label="Zoom to details of document locations"
          className={MAP_CONTROL_BUTTON_CLASS}>
          Zoom to details
        </button>
        <button
          type="button"
          onClick={handleResetView}
          aria-label="Reset map to city overview"
          className={MAP_CONTROL_BUTTON_CLASS}>
          Reset map
        </button>
      </div>

      <GoogleMap
        mapContainerClassName="h-full min-h-[440px] w-full xl:min-h-[600px]"
        center={markerGroups[0].location}
        zoom={CITY_OVERVIEW_ZOOM}
        options={MAP_OPTIONS}
        onLoad={handleMapLoad}
        onUnmount={handleMapUnmount}>
        {locationCoverage && (
          <CircleF
            center={locationCoverage.center}
            radius={locationCoverage.radiusMetres}
            options={ACTIVITY_COVERAGE_OPTIONS}
          />
        )}
        {markerGroups.map((marker) => (
          <NumberedMarker
            key={marker.id}
            marker={marker}
            selected={marker.id === selectedMarkerId}
            onSelect={() => onMarkerSelect(marker.id)}
          />
        ))}
        {selectedMarker && selectedMarkerItems.length > 0 && (
          <InfoWindowF
            position={selectedMarker.location}
            options={{ maxWidth: 300 }}
            onCloseClick={onMarkerClose}>
            <SelectedMarkerDetails items={selectedMarkerItems} />
          </InfoWindowF>
        )}
      </GoogleMap>
    </div>
  );
};

const ActivityHistoryMap = (props: Props) => {
  const [authenticationError, setAuthenticationError] = useState(false);

  useEffect(() => {
    const previousAuthFailureHandler = window.gm_authFailure;
    const handleAuthFailure = () => setAuthenticationError(true);
    window.gm_authFailure = handleAuthFailure;

    return () => {
      if (window.gm_authFailure === handleAuthFailure) {
        window.gm_authFailure = previousAuthFailureHandler;
      }
    };
  }, []);

  if (props.markerGroups.length === 0) {
    return (
      <MapStatus
        title="No location data"
        description="Document submissions are listed in the timeline. Location was not captured for these uploads."
      />
    );
  }

  if (!props.apiKey) {
    return (
      <MapStatus
        title="Map unavailable"
        description="Set VITE_GOOGLE_MAPS_API_KEY to display document submission locations."
      />
    );
  }

  if (authenticationError) {
    return (
      <MapStatus
        title="Map unavailable"
        description="Google Maps could not be authorised. The document timeline is still available."
      />
    );
  }

  return <LoadedActivityHistoryMap {...props} />;
};

export default ActivityHistoryMap;
